<template>
<div class="modal-overflow">
  <div class="row">
    <div class="col-md-12">
      <div class="form-group">
        <label>Label</label>
        <input type="text"
               name="label"
               ref="label"
               class="form-control"
               @blur="generateFieldKey"
               v-validate="modelValidations.label"
               v-model.trim="formField.label">
        <small class="text-danger" v-show="errors.has('label')">
          {{ getError('label') }}
        </small>
      </div>
      <div class="form-group">
        <label style="display:inline">Key</label>
        <span v-if="fieldInUse()" class="in-use-lock">Locked: In Use</span>
        <input type="text"
               :disabled="fieldInUse()"
               class="form-control"
               name="key"
               v-validate="modelValidations.key"
               v-model.trim="formField.key">
        <small class="text-danger" v-show="errors.has('key')">
          {{ getError('key') }}
        </small>
      </div>
      <div class="form-group">
        <label style="display:inline">Input Type</label>
        <span v-if="fieldInUse()" class="in-use-lock">Locked: In Use</span>
        <el-select class="select"
                   size="large"
                   :disabled="fieldInUse()"
                   placeholder="Input Type"
                   v-model="formField.component"
                   @change="toggleFieldVisibility">
          <el-option v-for="option in formComponents"
                     class="select"
                     :value="option.value"
                     :label="option.label"
                     :key="option.label" />
        </el-select>
        <small class="text-danger" v-show="errors.has('component')">
          {{ getError('component') }}
        </small>
      </div>
      <div class="form-group" v-if="inputWithPlaceholderSelected">
        <label>Placeholder</label>
        <input type="text"
               class="form-control"
               name="placeholder"
               v-model.trim="formField.placeholder">
        <small class="text-danger" v-show="errors.has('placeholder')">
          {{ getError('placeholder') }}
        </small>
      </div>
      <div class="form-group" v-if="inputWithDefaultSelected">
        <label>Default Value</label>
        <input type="text"
               name="defaultValue"
               class="form-control"
               v-model.trim="formField.defaultValue">
        <small class="text-danger" v-show="errors.has('pattern')">
          {{ getError('pattern') }}
        </small>
      </div>
      <div class="form-group">
        <label>Required</label>
        <p-switch type="primary"
                  on-text="YES"
                  off-text="NO"
                  style="display:block"
                  v-model="formField.required"></p-switch>
      </div>
      <div class="form-group" v-if="!inputWithValuesSelected && !inputNoUserEditableValue">
        <label>Min Length</label>
        <input type="number"
               name="min"
               class="form-control"
               min="0"
               v-model="formField.min" />
        <small class="text-danger" v-show="errors.has('min')">
          {{ getError('min') }}
        </small>
      </div>
      <div class="form-group" v-if="!inputWithValuesSelected && !inputNoUserEditableValue">
        <label>Max Length</label>
        <input type="number"
               name="max"
               class="form-control"
               min="0"
               v-model="formField.max" />
        <small class="text-danger" v-show="errors.has('max')">
          {{ getError('max') }}
        </small>
      </div>
      <div class="form-group" v-if="!inputWithValuesSelected && !inputNoUserEditableValue">
        <label>Validation Pattern (regex)</label>
        <input type="text"
               name="pattern"
               class="form-control"
               v-model="formField.pattern" />
        <small class="text-danger" v-show="errors.has('pattern')">
          {{ getError('pattern') }}
        </small>
      </div>
      <div class="form-group field-values" v-if="inputWithValuesSelected">
        <h6>Field Values</h6>
        <div class="add-value-container row">
          <div class="col-md-5">
            <label>Label</label>
            <input type="text"
                   name="inputValue"
                   class="form-control"
                   @blur="generateOptionValue"
                   v-model="optionValue.label" />
          </div>
          <div class="col-md-5">
            <label>Value</label>
            <input type="text"
                   name="inputValueKey"
                   class="form-control"
                   v-model="optionValue.value" />
          </div>
          <div class="col-md-2">
            <button @click="addFieldValue" class="btn btn-fill btn-info btn-md btn-add-field-value">+</button>
          </div>
          <div style="clear:both"></div>
        </div>
        <div v-on:mouseover="maybeInitSortableOptionValues">
          <el-table id="field-option-values" class="table-striped"
                    row-key="id"
                    :data="formField.values"
                    row-class-name="draggable"
                    border
                    style="width: 100%">
            <el-table-column v-for="column in tableColumns"
                             :key="column.label"
                             :min-width="column.minWidth"
                             :prop="column.prop"
                             :label="column.label">
            </el-table-column>
            <el-table-column
              width="110"
              fixed="right"
              label="Actions">
              <template v-slot="props">
                <div style="text-align: center">
                  <p-button type="danger" size="sm" icon :disabled="fieldInUse()" @click="removeFieldValue(props.row)">
                    <i class="fa fa-times"></i>
                  </p-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div class="form-group">
        <button type="submit" :disabled="isSubmitDisabled" @click.prevent="validateAndSubmit" class="btn btn-fill btn-info btn-wd pull-right">Save Form Field</button>
        <div class="form-group form-group-active-label">
          <label class="active-label">Active</label>
          <p-switch v-model="formField.active" type="primary" on-text="YES" off-text="NO"></p-switch>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import PButton from 'src/components/UIComponents/Button'
  import PSwitch from 'src/components/UIComponents/Switch'
  import {camelize, notifySuccess, notifyError} from 'src/utilities/utils'
  import Sortable from 'sortablejs'
  import cheerio from 'cheerio'
  import {OK, CREATED} from 'http-status-codes'
  import _ from 'lodash'

  export default {
    props: ['data'],
    components: {
      PButton,
      PSwitch
    },
    data() {
      return {
        sortableOptionValuesInitialized: false,
        inputWithValuesSelected: false,
        inputWithPlaceholderSelected: true,
        inputWithDefaultSelected: true,
        inputNoUserEditableValue: false,
        isProcessing: false,
        tableColumns: [
          {
            prop: 'label',
            label: 'Label',
            minWidth: 150
          },
          {
            prop: 'value',
            label: 'Value',
            minWidth: 150
          }
        ],
        modelValidations: {
          label: {
            required: true,
            max: 128
          },
          key: {
            required: true,
            max: 128
          }
        },
        optionValue: {
          label: null,
          value: null
        },
        formField: {
          id: null,
          label: null,
          key: null,
          component: 'Input',
          placeholder: null,
          required: false,
          min: null,
          max: null,
          pattern: null,
          defaultValue: null,
          values: [],
          active: true,
          sort: 100,
          createdAt: null,
          createdBy: null,
          updatedAt: null,
          updatedBy: null
        }
      }
    },
    computed: {
      ...mapGetters({
        formComponents: 'ui/inspectorAppFormComponents',
        formComponentsWithValues: 'ui/formComponentsWithValues',
        formComponentsNoDefault: 'ui/formComponentsNoDefault',
        formComponentsNoPlaceholder: 'ui/formComponentsNoPlaceholder',
        formComponentsNoUserEditableValue: 'ui/formComponentsNoUserEditableValue'
      }),
      isSubmitDisabled() {
        return this.isProcessing
      }
    },
    methods: {
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      fieldInUse() {
        return false // TODO - implement
        // return this.$store.getters['inspectorApp/fields/fieldInUse'](this.field.key)
      },
      generateFieldKey() {
        this.formField.key = camelize(this.formField.label)
      },
      generateOptionValue() {
        this.optionValue.value = camelize(this.optionValue.label)
      },
      toggleFieldVisibility() {
        this.inputWithValuesSelected = false
        this.inputWithPlaceholderSelected = true
        this.inputWithDefaultSelected = true
        this.inputNoUserEditableValue = false

        if (this.formComponentsWithValues.includes(this.formField.component)) {
          this.inputWithValuesSelected = true
        }

        if (this.formComponentsNoDefault.includes(this.formField.component)) {
          this.inputWithDefaultSelected = false
        }

        if (this.formComponentsNoPlaceholder.includes(this.formField.component)) {
          this.inputWithPlaceholderSelected = false
        }

        if (this.formComponentsNoUserEditableValue.includes(this.formField.component)) {
          this.inputNoUserEditableValue = true
        }
      },
      async validateAndSubmit() {
        this.isProcessing = true

        if (await this.$validator.validateAll()) {
          const response = await this.$store.dispatch('save2', {
            endpoint: 'form-fields',
            data: this.formField
          })

          if (CREATED === response.status) {
            this.$store.commit('inspectorApp/formFields/addResult', response.data)
            notifySuccess(this, "Form Field Save Successfully")
            this.closeModal()
            return
          }

          if (OK === response.status) {
            this.$store.commit('inspectorApp/formFields/updateResult', response.data)
            notifySuccess(this, "Form Field Saved Successfully")
            this.closeModal()
            return
          }

          notifyError(this, 'Failed to Save Form Field', response.errors)
        }
        this.isProcessing = false
      },
      maybeInitSortableOptionValues () {
        if (!this.sortableOptionValuesInitialized) {
          const table = document.querySelector('#field-option-values .el-table__body-wrapper tbody')
          const self = this
          Sortable.create(table, {
            onEnd () {
              let sort = 1
              const sortedOptionValues = {}
              for (let i = 0; i < table.rows.length; i++) {
                const $ = cheerio.load(table.rows[i].cells[1].innerHTML)
                const property = $('div').text()
                sortedOptionValues[property] = sort
                sort++
              }
              const payload = {
                fieldKey: self.formField.key,
                optionValues: sortedOptionValues
              }
              self.$store.commit('tasks/templates/editFieldsValuesSort', payload)
            }
          })
          this.sortableOptionValuesInitialized = true
        }
      },
      addFieldValue() {
        this.formField.values.push(JSON.parse(JSON.stringify(this.optionValue)))
        this.optionValue = {
          label: null,
          value: null
        }
      },
      maybeSortFieldOptionValues() {
        if (this.formField.values) {
          this.formField.values.sort((a, b) => {
            if (a.sort > b.sort) { return 1 }
            if (a.sort < b.sort) { return -1 }
            return 0
          })
        }
      },
      removeFieldValue(fvToDelete) {
        this.formField.values = _.filter(this.formField.values, (fieldValue) => fieldValue.value !== fvToDelete.value)
      },
      closeModal() {
        this.$emit('close')
      },
      ifEditBindFields() {
        if (this.data) {
          this.formField = Object.assign(this.formField, this.data)
        }
      }
    },
    mounted() {
      this.ifEditBindFields()
      this.toggleFieldVisibility()
      this.maybeSortFieldOptionValues()
    }
  }
</script>

<style scoped>
  label {
    display: block;
  }
  .btn-add-field-value {
    margin-top: 29px;
  }
  .add-value-container {
    padding-bottom: 20px;
  }
  .in-use-lock {
    margin-left: 5px;
    background-color: #cc2127;
    color: white;
    padding: 2px 6px 2px 6px;
    border-radius: 10px
  }
</style>
